import classNames from 'classnames'
import React, { useState } from 'react'
import { useLiveViewStateStore, useZoomFocusStore } from '../LiveViewStore'
import { useModalStateStore } from '../../../utils/ModalStore/ModalStore'
import useLiveViewScreenshot from '../hooks/useLiveViewScreenshot'
import styles from './Tabs.module.css'

export const Tabs: React.FC<{
    fullscreen: boolean
    closeModalOnStopStream: () => void
}> = ({ fullscreen, closeModalOnStopStream }) => {
    const {
        selectedTab,
        setSelectedTab,
        setStreamStarted,
        baseURL,
        stationName,
        checkDirtyLens,
        setCheckDirtyLens,
        transferZoomAndFocusToLiveView,
        loadingZoomFocus,
        hdMode,
        setHdMode,
        hdLoading
    } = useLiveViewStateStore()
    const { openModal } = useModalStateStore()
    const [isDownloading, setIsDownloading] = useState(false)
    const { getScreenshot } = useLiveViewScreenshot()
    const currentZoom = useZoomFocusStore((state) => state.zoom)
    const currentFocus = useZoomFocusStore((state) => state.focus)
    return (
        <div className={styles.root}>
            <div className={styles.tabsList}>
                <TabItem
                    icon="center_focus_strong"
                    label="Zoom & Focus"
                    isActive={selectedTab === 'focusZoom'}
                    onClick={() => setSelectedTab('focusZoom')}
                />
                <TabItem
                    icon="settings_brightness"
                    label="Brightness"
                    isActive={selectedTab === 'brightness'}
                    onClick={() => setSelectedTab('brightness')}
                />
                <TabItem
                    icon="filter_b_and_w"
                    label="Black Mask"
                    isLast
                    isActive={selectedTab === 'blackMask'}
                    onClick={() => setSelectedTab('blackMask')}
                />
            </div>
            <div className={classNames(styles.actions, { [styles.fullscreen]: fullscreen })}>
                <div
                    className={classNames(styles.iconButton, { [styles.active]: checkDirtyLens })}
                    onClick={() => {
                        if (loadingZoomFocus) return
                        setCheckDirtyLens(!checkDirtyLens)

                        if (!checkDirtyLens) {
                            sessionStorage.setItem('tempZoom', currentZoom.toString())
                            sessionStorage.setItem('tempFocus', currentFocus.toString())
                            transferZoomAndFocusToLiveView({
                                zoom: 0,
                                focus: 1180,
                                autofocus: false
                            })
                        } else
                            transferZoomAndFocusToLiveView({
                                zoom: sessionStorage.getItem('tempZoom')
                                    ? Number(sessionStorage.getItem('tempZoom'))
                                    : 0,
                                focus: sessionStorage.getItem('tempFocus')
                                    ? Number(sessionStorage.getItem('tempFocus'))
                                    : 1180,
                                autofocus: false
                            })
                    }}
                    title="Dirty lens"
                >
                    <i
                        className={classNames(styles.iconColor, 'material-icons', {
                            [styles.muted]: loadingZoomFocus
                        })}
                    >
                        dirty_lens
                    </i>
                </div>
                <div
                    className={classNames(styles.iconButton, {
                        [styles.active]: hdMode,
                        [styles.disabled]: hdLoading
                    })}
                    onClick={() => {
                        if (hdLoading) return
                        setHdMode(!hdMode)
                    }}
                    title={hdLoading ? 'Loading HD snapshot…' : 'HD snapshot'}
                >
                    <i className={classNames('material-icons', { [styles.spin]: hdLoading })}>
                        {hdLoading ? 'autorenew' : 'hd'}
                    </i>
                </div>
                <div
                    className={classNames(styles.iconButton, { [styles.disabled]: isDownloading })}
                    onClick={async () => {
                        if (isDownloading) return
                        setIsDownloading(true)
                        try {
                            const link = document.createElement('a')
                            link.href = await getScreenshot()
                            link.download = stationName ? `${stationName}.jpeg` : 'live_view_image.jpeg'
                            link.click()
                            URL.revokeObjectURL(link.href)
                        } catch (error) {
                            console.error('Error downloading image:', error)
                        } finally {
                            setIsDownloading(false)
                        }
                    }}
                    title="Download screenshot"
                >
                    <i className="material-icons">camera_alt</i>
                </div>
                <div
                    className={styles.iconButton}
                    onClick={() => {
                        setStreamStarted(baseURL as string, false)
                        if (fullscreen) {
                            closeModalOnStopStream()
                        }
                    }}
                    title="Stop stream"
                >
                    <i className="material-icons">stop</i>
                </div>
                {!fullscreen && (
                    <div
                        className={styles.iconButton}
                        onClick={() => {
                            openModal('LiveViewFullscreenModal')
                        }}
                        title="Go fullscreen"
                    >
                        <i className="material-icons">fullscreen</i>
                    </div>
                )}
            </div>
        </div>
    )
}
const TabItem: React.FC<{
    icon: string
    label: string
    isLast?: boolean
    isActive: boolean
    onClick: () => void
}> = ({ icon, label, isLast = false, isActive, onClick }) => {
    return (
        <div
            className={classNames(styles.tabItem, {
                [styles.active]: isActive,
                [styles.last]: isLast
            })}
            onClick={onClick}
        >
            <i className="material-icons">{icon}</i>
            <span className={styles.tabItemLabel}>{label}</span>
        </div>
    )
}
